/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-useless-escape */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import update from 'immutability-helper';
import React, { useEffect, useState } from 'react';
import { getMatrixAndLossType, getVent } from '../../../api/product-api';
import {
    IAudiogram,
    IAudiogramResponse,
    IProductDetail,
} from '../../../models/product-detail';
import { IPatient } from '../../../models/prosites';
import { findIcon, thresholds as frequency } from '../../common/constants';
import RowElementWrapper from '../../common/row-element-wrapper';
import SpeechElement from './speech-element';
import { ProductOrderFormErrors } from './validation';
import { APCustomCasedDesignIDs } from '../../common/constants';
import { useIntl } from 'react-intl';

export const getRoundedAudiogramValue = (x: number) => {
    if (isNaN(x)) return null;
    const y = x;

    const z = Math.round(y / 5) * 5;
    return z < -10 ? -10 : z > 130 ? 130 : z;
};

interface IThresholdSectionProps {
    selectedSide: string;
    patientDetails: IPatient;
    onChange: (patientDetails: IPatient) => void;
    productDetails: IProductDetail | null;
    updateStarkeyChoiceOrHearingLoss: (
        right: IAudiogramResponse,
        left: IAudiogramResponse
    ) => void;
    audiogramResponse?: IAudiogramResponse;
    errors: ProductOrderFormErrors;
}

const ThresholdSection = ({
    selectedSide,
    patientDetails,
    onChange,
    productDetails,
    updateStarkeyChoiceOrHearingLoss,
    audiogramResponse,
    errors,
}: IThresholdSectionProps) => {
    const intl = useIntl();
    let thresholdSides: string[] = [];
    let speechSides: string[] = [];
    if (selectedSide === 'R') {
        thresholdSides = ['Right'];
        speechSides = ['Right (dB)'];
    }
    if (selectedSide === 'L') {
        thresholdSides = ['Left'];
        speechSides = ['Left (dB)'];
    }
    if (selectedSide === 'B') {
        thresholdSides = ['Right', 'Left'];
        speechSides = ['Right (dB)', 'Binaural (dB)', 'Left (dB)'];
    }

    const [audiogram, setAudiogram] = useState<IAudiogram>(
        JSON.parse(patientDetails!.audiogram)
    );

    const [lossType, setLossType] = useState<{
        right: IAudiogramResponse | undefined | null;
        left: IAudiogramResponse | undefined | null;
    }>({ right: patientDetails!.rightLoss, left: patientDetails.leftLoss });

    const [thresholdRightAC, setThresholdRightAC] = useState<
        string[] | undefined[]
    >();
    const [thresholdRightBC, setThresholdRightBC] = useState<
        string[] | undefined[]
    >();
    const [thresholdLeftAC, setThresholdLeftAC] = useState<
        string[] | undefined[]
    >();
    const [thresholdLeftBC, setThresholdLeftBC] = useState<
        string[] | undefined[]
    >();

    const [isExistingFirstLoad, setIsExistingFirstLoad] = useState<boolean>(
        productDetails?.existing?.patient?.audiogram ===
            JSON.stringify(audiogram)
    );

    //converts numbers into chars
    const encodeCurve = (response: number, noResponse: boolean) => {
        const i = response;
        let c = '';
        if (isNaN(i)) c = '';
        else if (i === 0) c = '0';
        else if (i === -5) c = '1';
        else if (i === -10) c = '2';
        else
            c = String.fromCharCode(Math.round(i / 5) + (noResponse ? 96 : 64));
        return c;
    };

    //converts chars into numbers
    const decodeCurve = (code: string) => {
        if (!code) return undefined;
        let response: number | undefined = undefined;
        // 'A' =  5 dB, 'B' =  10 dB, ... 'Z' =  130 dB
        // 'a' = *5 dB, 'b' = *10 dB, ... 'z' = *130 dB
        // '0' =  0 dB, '1' =  -5 dB,     '2' =  -10 dB
        const c = code.charCodeAt(0);
        if (code === '0') response = 0;
        if (code === '1') response = -5;
        if (code === '2') response = -10;
        if (code >= 'A' && code <= 'Z') response = (c - 64) * 5;
        if (code >= 'a' && code <= 'z') {
            response = (c - 96) * 5;
        }
        return response;
    };

    const getDecodedValue = (
        side: string,
        conduction: string,
        threshold: string
    ) => {
        let value: number | undefined = undefined;
        switch (side) {
            case 'left':
                if (conduction === 'air') {
                    const abc = audiogram.left.air.curve.split(',');
                    value = decodeCurve(abc[frequency.indexOf(threshold)]);
                } else {
                    const abc = audiogram.left.bone.curve.split(',');
                    value = decodeCurve(abc[frequency.indexOf(threshold)]);
                }
                return value;
            case 'right':
                if (conduction === 'air') {
                    const abc = audiogram.right.air.curve.split(',');
                    value = decodeCurve(abc[frequency.indexOf(threshold)]);
                } else {
                    const abc = audiogram.right.bone.curve.split(',');
                    value = decodeCurve(abc[frequency.indexOf(threshold)]);
                }
                return value;
            default:
                return value;
        }
    };

    const getEncodedValue = (
        side: string,
        conduction: string,
        threshold: string
    ) => {
        let value: string | undefined = undefined;
        switch (side) {
            case 'left':
                if (conduction === 'air') {
                    const abc = audiogram.left.air.curve.split(',');
                    value = abc[frequency.indexOf(threshold)];
                } else {
                    const abc = audiogram.left.bone.curve.split(',');
                    value = abc[frequency.indexOf(threshold)];
                }
                return value;
            case 'right':
                if (conduction === 'air') {
                    const abc = audiogram.right.air.curve.split(',');
                    value = abc[frequency.indexOf(threshold)];
                } else {
                    const abc = audiogram.right.bone.curve.split(',');
                    value = abc[frequency.indexOf(threshold)];
                }
                return value;
            default:
                return value;
        }
    };

    const createCurve = (
        previousCurve: string,
        changeAt: string,
        newValue: number,
        eventType: string,
        isNoResponse?: boolean
    ) => {
        let newCurve = '';
        let indexCounter = 0;
        const responseArray = previousCurve.split(',');

        const noResponse =
            eventType === 'blur'
                ? responseArray[frequency.indexOf(changeAt)] !== '' &&
                  !/^\-?[0-9]\d{0,2}$/.test(
                      responseArray[frequency.indexOf(changeAt)]
                  ) &&
                  responseArray[frequency.indexOf(changeAt)] ===
                      responseArray[frequency.indexOf(changeAt)].toLowerCase()
                : (isNoResponse! &&
                      responseArray[frequency.indexOf(changeAt)] === '') ||
                  (isNoResponse! &&
                      responseArray[frequency.indexOf(changeAt)] !== '' &&
                      responseArray[frequency.indexOf(changeAt)] ===
                          responseArray[
                              frequency.indexOf(changeAt)
                          ].toUpperCase());

        const inRangevalue =
            newValue < -10 ? -10 : newValue > 130 ? 130 : newValue;

        responseArray[frequency.indexOf(changeAt)] = encodeCurve(
            inRangevalue,
            noResponse
        );

        responseArray.map(
            (x) => (newCurve += x + (indexCounter++ >= 9 ? '' : ','))
        );
        return newCurve;
    };

    const isNR = (side: string, conduction: string, threshold: string) => {
        return (
            getEncodedValue(side.toLowerCase(), conduction, threshold) !== '' &&
            !/^\-?[0-9]\d{0,2}$/.test(
                getEncodedValue(side.toLowerCase(), conduction, threshold)!
            ) &&
            getEncodedValue(side.toLowerCase(), conduction, threshold) ===
                getEncodedValue(
                    side.toLowerCase(),
                    conduction,
                    threshold
                )!.toLowerCase()
        );
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.persist();
        const eventValue = event.target.value;
        const eventId = event.target.id;
        const isProceedToOnChange =
            (/^[-0-9n\b]+$/.test(eventValue) || eventValue === '') &&
            (eventValue.length <= 3 || eventValue.endsWith('n'));

        if (isProceedToOnChange) {
            if (event.type === 'change') {
                switch (eventId.split('-')[0]) {
                    case 'Right':
                        if (eventId.split('-')[1] === 'ac') {
                            setThresholdRightAC((prevState) =>
                                update(prevState, {
                                    [frequency.indexOf(eventId.split('-')[2])]:
                                        { $set: eventValue },
                                })
                            );
                        } else {
                            setThresholdRightBC((prevState) =>
                                update(prevState, {
                                    [frequency.indexOf(eventId.split('-')[2])]:
                                        { $set: eventValue },
                                })
                            );
                        }
                        break;
                    case 'Left':
                        if (eventId.split('-')[1] === 'ac') {
                            setThresholdLeftAC((prevState) =>
                                update(prevState, {
                                    [frequency.indexOf(eventId.split('-')[2])]:
                                        { $set: eventValue },
                                })
                            );
                        } else {
                            setThresholdLeftBC((prevState) =>
                                update(prevState, {
                                    [frequency.indexOf(eventId.split('-')[2])]:
                                        { $set: eventValue },
                                })
                            );
                        }
                        break;
                    default:
                        break;
                }
            }
            if (event.type === 'blur' || eventValue.includes('n')) {
                switch (eventId.split('-')[0]) {
                    case 'Right':
                        if (eventId.split('-')[1] === 'ac') {
                            setAudiogram((prevState) => ({
                                ...prevState,
                                right: {
                                    ...prevState.right,
                                    air: {
                                        ...prevState.right.air,
                                        curve: createCurve(
                                            prevState.right.air.curve,
                                            eventId.split('-')[2],
                                            parseInt(
                                                thresholdRightAC![
                                                    frequency.indexOf(
                                                        eventId.split('-')[2]
                                                    )
                                                ]!
                                            ),
                                            event.type,
                                            eventValue.includes('n')
                                        ),
                                    },
                                },
                            }));
                        } else {
                            setAudiogram((prevState) => ({
                                ...prevState,
                                right: {
                                    ...prevState.right,
                                    bone: {
                                        ...prevState.right.bone,
                                        curve: createCurve(
                                            audiogram.right.bone.curve,
                                            eventId.split('-')[2],
                                            parseInt(
                                                thresholdRightBC![
                                                    frequency.indexOf(
                                                        eventId.split('-')[2]
                                                    )
                                                ]!
                                            ),
                                            event.type,
                                            eventValue.includes('n')
                                        ),
                                    },
                                },
                            }));
                        }
                        break;
                    case 'Left':
                        if (eventId.split('-')[1] === 'ac') {
                            setAudiogram((prevState) => ({
                                ...prevState,
                                left: {
                                    ...prevState.left,
                                    air: {
                                        ...prevState.left.air,
                                        curve: createCurve(
                                            prevState.left.air.curve,
                                            eventId.split('-')[2],
                                            parseInt(
                                                thresholdLeftAC![
                                                    frequency.indexOf(
                                                        eventId.split('-')[2]
                                                    )
                                                ]!
                                            ),
                                            event.type,
                                            eventValue.includes('n')
                                        ),
                                    },
                                },
                            }));
                        } else {
                            setAudiogram((prevState) => ({
                                ...prevState,
                                left: {
                                    ...prevState.left,
                                    bone: {
                                        ...prevState.left.bone,
                                        curve: createCurve(
                                            prevState.left.bone.curve,
                                            eventId.split('-')[2],
                                            parseInt(
                                                thresholdLeftBC![
                                                    frequency.indexOf(
                                                        eventId.split('-')[2]
                                                    )
                                                ]!
                                            ),
                                            event.type,
                                            eventValue.includes('n')
                                        ),
                                    },
                                },
                            }));
                        }
                        break;
                    default:
                        break;
                }
            }
        }
    };

    const copySides = (copyFrom: string) => {
        if (copyFrom === 'Left') {
            setAudiogram((prevState) => ({
                ...prevState,
                right: { ...prevState.left },
            }));
        } else {
            setAudiogram((prevState) => ({
                ...prevState,
                left: { ...prevState.right },
            }));
        }
    };

    const updateSpeech = (
        event: React.ChangeEvent<HTMLInputElement>,
        speechType: string,
        side: string
    ) => {
        event.persist();
        if (
            event.target.value === '' ||
            /^[0-9\b]+$/.test(event.target.value)
        ) {
            switch (speechType) {
                case 'MCL':
                    if (side.includes('Left')) {
                        setAudiogram((prevState) => ({
                            ...prevState,
                            left: {
                                ...prevState.left,
                                MCL:
                                    event.type === 'change'
                                        ? event.target.value === ''
                                            ? null
                                            : event.target.value
                                        : getRoundedAudiogramValue(
                                              parseInt(event.target.value)
                                          ),
                            },
                        }));
                    }
                    if (side.includes('Right')) {
                        setAudiogram((prevState) => ({
                            ...prevState,
                            right: {
                                ...prevState.right,
                                MCL:
                                    event.type === 'change'
                                        ? event.target.value === ''
                                            ? null
                                            : event.target.value
                                        : getRoundedAudiogramValue(
                                              parseInt(event.target.value)
                                          ),
                            },
                        }));
                    }
                    if (side.includes('Binaural')) {
                        setAudiogram((prevState) => ({
                            ...prevState,
                            MCL:
                                event.type === 'change'
                                    ? event.target.value === ''
                                        ? null
                                        : event.target.value
                                    : getRoundedAudiogramValue(
                                          parseInt(event.target.value)
                                      ),
                        }));
                    }
                    break;
                case 'UCL':
                    if (side.includes('Left')) {
                        setAudiogram((prevState) => ({
                            ...prevState,
                            left: {
                                ...prevState.left,
                                UCL:
                                    event.type === 'change'
                                        ? event.target.value === ''
                                            ? null
                                            : event.target.value
                                        : getRoundedAudiogramValue(
                                              parseInt(event.target.value)
                                          ),
                            },
                        }));
                    }
                    if (side.includes('Right')) {
                        setAudiogram((prevState) => ({
                            ...prevState,
                            right: {
                                ...prevState.right,
                                UCL:
                                    event.type === 'change'
                                        ? event.target.value
                                        : getRoundedAudiogramValue(
                                              parseInt(event.target.value)
                                          ),
                            },
                        }));
                    }
                    if (side.includes('Binaural')) {
                        setAudiogram((prevState) => ({
                            ...prevState,
                            UCL:
                                event.type === 'change'
                                    ? event.target.value
                                    : getRoundedAudiogramValue(
                                          parseInt(event.target.value)
                                      ),
                        }));
                    }
                    break;
                default:
                    break;
            }
        }
    };

    const getLossType = async () => {
        const rightvalues: IAudiogramResponse = { ...audiogramResponse! };
        const leftvalues: IAudiogramResponse = { ...audiogramResponse! };
        // Task 111962 - add const's to help determine if we are AP receiver or RIC with AP
        const isAP =
            APCustomCasedDesignIDs.includes(
                productDetails!.designId ? productDetails!.designId : 0
            ) ||
            (productDetails &&
                productDetails.ricAPOptions &&
                productDetails!.ricAPOptions!.length > 0)
                ? true
                : false;

        try {
            // Task 111962 - added isAP to call
            const rightMatrix = await getMatrixAndLossType(
                productDetails!.catalogId,
                productDetails!.designId ? productDetails!.designId : 1,
                audiogram.right.air.curve,
                isAP
            );
            if (rightMatrix.ok) {
                rightvalues!.matrix = rightMatrix.data!.matrix;
                rightvalues!.loss = rightMatrix.data!.loss;
            }
        } catch {
            // Do nothing here ... we need to call setLossType below
        }
        try {
            // Task 111962 - added isAP to call
            const leftMatrix = await getMatrixAndLossType(
                productDetails!.catalogId,
                productDetails!.designId ? productDetails!.designId : 1,
                audiogram.left.air.curve,
                isAP
            );
            if (leftMatrix.ok) {
                leftvalues!.matrix = leftMatrix.data!.matrix;
                leftvalues!.loss = leftMatrix.data!.loss;
            }
        } catch {
            // Do nothing here ... we need to call setLossType below
        }
        const ra500 = getDecodedValue('right', 'air', '500');
        const ra1k = getDecodedValue('right', 'air', '1k');
        if (ra500 !== undefined || ra1k !== undefined) {
            try {
                const rightBestVent = await getVent(
                    productDetails!.catalogId,
                    productDetails!.designId ? productDetails!.designId : 1,
                    ra500,
                    ra1k
                );
                if (rightBestVent.data) {
                    rightvalues!.vent = rightBestVent.data!;
                }
            } catch {
                // Do nothing here ... we need to call setLossType below
            }
        }
        const la500 = getDecodedValue('left', 'air', '500');
        const la1k = getDecodedValue('left', 'air', '1k');
        if (la500 !== undefined || la1k !== undefined) {
            try {
                const leftBestVent = await getVent(
                    productDetails!.catalogId,
                    productDetails!.designId ? productDetails!.designId : 1,
                    la500,
                    la1k
                );
                if (leftBestVent.data) {
                    leftvalues!.vent = leftBestVent.data!;
                }
            } catch {
                // Do nothing here ... we need to call setLossType below
            }
        }

        setLossType({ right: rightvalues, left: leftvalues });
    };

    useEffect(() => {
        setThresholdRightAC(
            audiogram.right.air.curve
                .split(',')
                .map((x) =>
                    decodeCurve(x) || decodeCurve(x) === 0
                        ? decodeCurve(x)!.toString()
                        : ''
                )
        );

        setThresholdRightBC(
            audiogram.right.bone.curve
                .split(',')
                .map((x) =>
                    decodeCurve(x) || decodeCurve(x) === 0
                        ? decodeCurve(x)!.toString()
                        : ''
                )
        );

        setThresholdLeftAC(
            audiogram.left.air.curve
                .split(',')
                .map((x) =>
                    decodeCurve(x) || decodeCurve(x) === 0
                        ? decodeCurve(x)!.toString()
                        : ''
                )
        );

        setThresholdLeftBC(
            audiogram.left.bone.curve
                .split(',')
                .map((x) =>
                    decodeCurve(x) || decodeCurve(x) === 0
                        ? decodeCurve(x)!.toString()
                        : ''
                )
        );

        //TODO: need to figure out a way to run getLossType() only when there is an audiogram change and also once when loading the product from the cart
        if (
            (!patientDetails.rightLoss ||
                !patientDetails.leftLoss ||
                patientDetails.audiogram !== JSON.stringify(audiogram)) &&
            !isExistingFirstLoad
        ) {
            getLossType();
        }
        setIsExistingFirstLoad(false);
    }, [audiogram]);

    useEffect(() => {
        onChange({
            ...patientDetails!,
            audiogram: JSON.stringify(audiogram),
            rightLoss: lossType.right!,
            leftLoss: lossType.left!,
        });
    }, [lossType]);

    // TODO, this is causing an extra run rules on initial load for picaso.
    useEffect(() => {
        updateStarkeyChoiceOrHearingLoss(lossType.right!, lossType.left!);
    }, [patientDetails.rightLoss, patientDetails.leftLoss]);

    return (
        <>
            {thresholdRightAC &&
                thresholdRightBC &&
                thresholdLeftAC &&
                thresholdLeftBC && (
                    <>
                        {thresholdSides.map((side) => {
                            return (
                                <div key={side + 'Threshold'}>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <h4>
                                                <FontAwesomeIcon
                                                    icon={findIcon(
                                                        'fal',
                                                        'ear'
                                                    )}
                                                    className={`ear-icon-${
                                                        side === 'Right'
                                                            ? 'red'
                                                            : 'blue'
                                                    }`}
                                                />
                                                <i />{' '}
                                                {intl.formatMessage({
                                                    id: `${side}_Ear`,
                                                    defaultMessage: `${side}_Ear`,
                                                })}
                                            </h4>
                                            {selectedSide === 'B' && (
                                                <h4
                                                    className="pull-right"
                                                    style={{
                                                        marginTop: '-30px',
                                                    }}
                                                >
                                                    <a
                                                        onClick={() =>
                                                            copySides(side)
                                                        }
                                                        id="audiogram-copy-to-side-btn"
                                                    >
                                                        {side === 'Right'
                                                            ? intl.formatMessage(
                                                                  {
                                                                      id: 'Copy_to_Left',
                                                                      defaultMessage:
                                                                          'Copy to Left',
                                                                  }
                                                              )
                                                            : intl.formatMessage(
                                                                  {
                                                                      id: 'Copy_to_Right',
                                                                      defaultMessage:
                                                                          'Copy to Right',
                                                                  }
                                                              )}
                                                    </a>
                                                </h4>
                                            )}
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div
                                            className="col-md-1 col-sm-1 col-xs-1"
                                            style={{
                                                textAlign: 'right',
                                                fontSize: '15px',
                                                marginTop: '10px',
                                            }}
                                        >
                                            <br />
                                            <p>AC</p>
                                        </div>
                                        {frequency.map((threshold) => {
                                            return (
                                                <div
                                                    className={`col-md-1 col-sm-1 col-xs-1 text-center ${
                                                        Object.keys(
                                                            errors
                                                        ).find(
                                                            (x) =>
                                                                x ===
                                                                `${side.toLowerCase()}${threshold}`
                                                        )
                                                            ? 'has-error'
                                                            : ''
                                                    }`}
                                                    key={threshold}
                                                >
                                                    <label
                                                        className={
                                                            'control-label'
                                                        }
                                                    >
                                                        {' '}
                                                        {threshold}
                                                    </label>
                                                    <br />
                                                    <input
                                                        id={
                                                            side +
                                                            '-ac-' +
                                                            threshold
                                                        }
                                                        className="form-control text-center"
                                                        type="text"
                                                        value={
                                                            side === 'Right'
                                                                ? thresholdRightAC![
                                                                      frequency.indexOf(
                                                                          threshold
                                                                      )
                                                                  ]
                                                                : thresholdLeftAC![
                                                                      frequency.indexOf(
                                                                          threshold
                                                                      )
                                                                  ]
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleChange}
                                                        required={[
                                                            '500',
                                                            '1k',
                                                            '2k',
                                                            '4k',
                                                        ].includes(threshold)}
                                                    />

                                                    {isNR(
                                                        side,
                                                        'air',
                                                        threshold
                                                    ) && (
                                                        <div className="input-header-NR-A">
                                                            <div>N.R</div>
                                                        </div>
                                                    )}
                                                    {[
                                                        '500',
                                                        '1k',
                                                        '2k',
                                                        '4k',
                                                    ].includes(threshold) && (
                                                        <span
                                                            className="required"
                                                            style={{
                                                                right: '17px',
                                                                top: '22px',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="row">
                                        <div
                                            className="col-md-1 col-sm-1 col-xs-1"
                                            style={{
                                                textAlign: 'right',
                                                fontSize: '15px',
                                                marginTop: '10px',
                                            }}
                                        >
                                            <p>BC</p>
                                        </div>
                                        {frequency.map((threshold) => {
                                            return (
                                                <div
                                                    className="col-md-1 col-sm-1 col-xs-1 text-center"
                                                    key={threshold}
                                                >
                                                    <input
                                                        id={
                                                            side +
                                                            '-bc-' +
                                                            threshold
                                                        }
                                                        className="form-control text-center"
                                                        value={
                                                            side === 'Right'
                                                                ? thresholdRightBC![
                                                                      frequency.indexOf(
                                                                          threshold
                                                                      )
                                                                  ]
                                                                : thresholdLeftBC![
                                                                      frequency.indexOf(
                                                                          threshold
                                                                      )
                                                                  ]
                                                        }
                                                        type="text"
                                                        onBlur={handleChange}
                                                        onChange={handleChange}
                                                    />
                                                    {isNR(
                                                        side,
                                                        'bone',
                                                        threshold
                                                    ) && (
                                                        <div className="input-header-NR-B">
                                                            <div>N.R</div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div
                                        className="row"
                                        style={{
                                            marginLeft: '80px',
                                            fontSize: '11px',
                                        }}
                                    >
                                        <i>
                                            {intl.formatMessage({
                                                id: 'After_entering_a_threshold',
                                                defaultMessage: `After entering a threshold value
                                            press "n" to add or remove "No
                                            Response" to the entered threshold
`,
                                            })}
                                        </i>
                                    </div>
                                </div>
                            );
                        })}
                        <RowElementWrapper>
                            <SpeechElement
                                audiogram={audiogram}
                                speechType="MCL"
                                sides={speechSides}
                                onChange={updateSpeech}
                            />
                        </RowElementWrapper>
                        <RowElementWrapper>
                            <SpeechElement
                                audiogram={audiogram}
                                speechType="UCL"
                                sides={speechSides}
                                onChange={updateSpeech}
                            />
                        </RowElementWrapper>
                    </>
                )}
        </>
    );
};

export default ThresholdSection;
